import _ from 'lodash'
import { PHASE_ORDER } from '../generators/generate-preview-schedule-data'
import { CROP_TASK_TYPES } from '../constants/crop-modal-constants'

export const CROP_MODAL_ACTION_TYPES = {
  SET_SPECIES: 'set-species',
  SET_VARIETY: 'set-variety',
  SET_COUNT: 'set-count',
  SET_INDOOR_OFFSET: 'set-indoor-offset',
  SET_OUTDOOR_OFFSET: 'set-outdoor-offset',
  SET_DTM: 'set-dtm',
  SET_GROWTH_TYPE: 'set-growth-type',
  SET_LIFECYCLE_TYPE: 'set-lifecycle-type',
  SET_PROPAGATION_CODE: 'set-propagation-code',
  SET_SELECTED_COLLECTIONS: 'set-selected-collections',
  TOGGLE_PHASE_CODE: 'toggle-phase-code',
  TOGGLE_TASK_CODE: 'toggle-task-code',
  SET_SELECTED_PHASE_CODES: 'set-selected-phase-codes',
  SET_SELECTED_TASK_CODES: 'set-selected-task-codes',
  SET_ADJUSTED_PHASES: 'set-adjusted-phases',
  SET_ADJUSTED_TASKS: 'set-adjusted-tasks',
  SET_NOTES: 'set-notes',
  SET_TASK_NOTES: 'set-task-notes',
  SET_SHOW_NOTES: 'set-show-notes',
  SET_SHOW_CROP_SCHEDULE: 'set-show-crop-schedule',
  SET_USER_ORG: 'set-user-org',
  SET_SPECIES_TOUCHED: 'set-species-touched',
  SET_SUCCESSIONS: 'set-successions',
  LOAD_EDIT_CROP: 'load-edit-crop',
  SET_BULK_ADD: 'set-bulk-add',
  ENQUEUE_CROP: 'enqueue-crop',
  RESET_FORM: 'reset-form',
  RESET: 'reset',
  // Custom crop
  SET_CUSTOM_CROP_MODE: 'set-custom-crop-mode',
  SET_CUSTOM_SPECIES_NAME: 'set-custom-species-name',
  SET_CUSTOM_VARIETY_MODE: 'set-custom-variety-mode',
  SET_CUSTOM_VARIETY_NAME: 'set-custom-variety-name',
  SET_CUSTOM_FAMILY_CODE: 'set-custom-family-code',
  SET_ARCHETYPE: 'set-archetype',
}

export const initialState = {
  archetype: null,
  species: null,
  variety: null,
  count: 1,
  indoorOffset: null,
  outdoorOffset: null,
  dtm: null,
  growthType: null,
  lifecycleType: null,
  cropCreatedAt: null,
  notes: '',
  taskNotes: {},
  showCropSchedule: false,
  showNotes: false,
  selectedCollections: ['core'],
  adjustedPhases: null,
  adjustedTasks: null,
  selectedPhaseCodes: null,
  selectedTaskCodes: null,
  selectedPropagationCode: null,
  userSelectedOrgId: null,
  userSelectedOrgName: null,
  speciesTouched: false,
  successions: { enabled: false, count: 1, intervalDays: 14 },
  bulkAdd: false,
  newCropsQueue: [],
  // Custom crop
  isCustomCrop: false,
  customSpeciesName: '',
  isCustomVariety: false,
  customVarietyName: '',
  customFamilyCode: null,
}

export const CropModalContextReducer = (state, action) => {
  switch (action.type) {
    case CROP_MODAL_ACTION_TYPES.SET_SPECIES: {
      const { species, archetype } = action
      return {
        ...state,
        species,
        archetype,
        variety: null,
        growthType: species?.growth_code ?? null,
        lifecycleType: species?.lifecycle_code ?? null,
        dtm: archetype?.dtm ?? null,
        indoorOffset: archetype?.indoor_sow_offset ?? null,
        outdoorOffset: archetype?.outdoor_move_offset ?? null,
        selectedPhaseCodes: null,
        selectedTaskCodes: null,
        selectedPropagationCode: null,
        adjustedPhases: null,
        adjustedTasks: null,
        // Clear custom crop state when a template species is selected
        isCustomCrop: false,
        customSpeciesName: '',
        customFamilyCode: null,
      }
    }

    case CROP_MODAL_ACTION_TYPES.SET_VARIETY: {
      const { variety } = action
      const effectiveTemplate = variety ?? state.species
      return {
        ...state,
        variety,
        growthType: effectiveTemplate?.growth_code ?? null,
        lifecycleType: effectiveTemplate?.lifecycle_code ?? null,
        dtm: null,  // Reset so effectiveTemplate.dtm takes over in preview
        adjustedPhases: null,
        adjustedTasks: null,
      }
    }

    case CROP_MODAL_ACTION_TYPES.SET_COUNT:
      return { ...state, count: action.count }

    case CROP_MODAL_ACTION_TYPES.SET_INDOOR_OFFSET:
      return { ...state, indoorOffset: action.indoorOffset }

    case CROP_MODAL_ACTION_TYPES.SET_OUTDOOR_OFFSET:
      return { ...state, outdoorOffset: action.outdoorOffset }

    case CROP_MODAL_ACTION_TYPES.SET_DTM:
      return { ...state, dtm: action.dtm }

    case CROP_MODAL_ACTION_TYPES.SET_GROWTH_TYPE:
      return { ...state, growthType: action.growthType }

    case CROP_MODAL_ACTION_TYPES.SET_LIFECYCLE_TYPE:
      return { ...state, lifecycleType: action.lifecycleType }

    case CROP_MODAL_ACTION_TYPES.SET_PROPAGATION_CODE:
      return {
        ...state,
        selectedPropagationCode: action.propagationCode,
        selectedTaskCodes: null,
        adjustedPhases: null,
        adjustedTasks: null,
        ...(state.isCustomCrop && { selectedPhaseCodes: null }),
      }

    case CROP_MODAL_ACTION_TYPES.SET_SELECTED_COLLECTIONS:
      return { ...state, selectedCollections: action.selectedCollections }

    case CROP_MODAL_ACTION_TYPES.TOGGLE_PHASE_CODE: {
      const { phaseCode, effectivePhaseCodes } = action
      const current = state.selectedPhaseCodes ?? effectivePhaseCodes ?? []
      const isRemoving = current.includes(phaseCode)

      const newCodes = isRemoving
        ? current.filter(code => code !== phaseCode)
        : _.sortBy([...current, phaseCode], code => PHASE_ORDER.indexOf(code))

      // Handler always dispatches setAdjustedPhases after this, so never clear here
      return { ...state, selectedPhaseCodes: newCodes }
    }

    case CROP_MODAL_ACTION_TYPES.TOGGLE_TASK_CODE: {
      const { taskCode, effectiveTaskCodes } = action
      const current = state.selectedTaskCodes ?? effectiveTaskCodes ?? []
      const taskOrder = CROP_TASK_TYPES.map(t => t.code)

      const newCodes = current.includes(taskCode)
        ? current.filter(code => code !== taskCode)
        : _.sortBy([...current, taskCode], code => taskOrder.indexOf(code))

      // Handler dispatches setAdjustedTasks surgically after this, so never clear here
      return { ...state, selectedTaskCodes: newCodes }
    }

    case CROP_MODAL_ACTION_TYPES.SET_SELECTED_PHASE_CODES:
      return { ...state, selectedPhaseCodes: action.selectedPhaseCodes }

    case CROP_MODAL_ACTION_TYPES.SET_SELECTED_TASK_CODES:
      return { ...state, selectedTaskCodes: action.selectedTaskCodes }

    case CROP_MODAL_ACTION_TYPES.SET_ADJUSTED_PHASES:
      return { ...state, adjustedPhases: action.adjustedPhases }

    case CROP_MODAL_ACTION_TYPES.SET_ADJUSTED_TASKS:
      return { ...state, adjustedTasks: action.adjustedTasks }

    case CROP_MODAL_ACTION_TYPES.SET_NOTES:
      return { ...state, notes: action.notes }

    case CROP_MODAL_ACTION_TYPES.SET_TASK_NOTES:
      return { ...state, taskNotes: { ...state.taskNotes, [action.taskCode]: action.notes } }

    case CROP_MODAL_ACTION_TYPES.SET_SHOW_NOTES:
      return { ...state, showNotes: action.showNotes }

    case CROP_MODAL_ACTION_TYPES.SET_SHOW_CROP_SCHEDULE:
      return { ...state, showCropSchedule: action.showCropSchedule }

    case CROP_MODAL_ACTION_TYPES.SET_USER_ORG:
      return {
        ...state,
        userSelectedOrgId: action.orgId,
        userSelectedOrgName: action.orgName,
      }

    case CROP_MODAL_ACTION_TYPES.SET_SPECIES_TOUCHED:
      return { ...state, speciesTouched: action.speciesTouched }

    case CROP_MODAL_ACTION_TYPES.SET_SUCCESSIONS:
      return { ...state, successions: action.successions }

    case CROP_MODAL_ACTION_TYPES.LOAD_EDIT_CROP: {
      const { crop, archetype, template, variety, count } = action
      const isCustomCrop = !crop.template_id
      return {
        ...state,
        archetype: archetype ?? state.archetype,
        species: isCustomCrop ? null : (template ?? state.species),
        variety: isCustomCrop ? null : (variety ?? null),
        count: count ?? state.count,
        growthType: crop.growth_code ?? null,
        lifecycleType: crop.lifecycle_code ?? null,
        cropCreatedAt: crop.created_at ?? null,
        notes: crop.notes ?? '',
        showNotes: Boolean(crop.notes),
        userSelectedOrgId: crop.organization_id ?? null,
        userSelectedOrgName: crop.organization?.name ?? null,
        isCustomCrop,
        customSpeciesName: isCustomCrop ? (crop.species_name ?? '') : '',
        isCustomVariety: false,
        customVarietyName: '',
        customFamilyCode: isCustomCrop ? (crop.family_code ?? null) : null,
      }
    }

    case CROP_MODAL_ACTION_TYPES.SET_BULK_ADD:
      return { ...state, bulkAdd: action.bulkAdd }

    case CROP_MODAL_ACTION_TYPES.ENQUEUE_CROP:
      return { ...state, newCropsQueue: [...state.newCropsQueue, action.cropEntry] }

    case CROP_MODAL_ACTION_TYPES.SET_CUSTOM_CROP_MODE: {
      const { isCustomCrop } = action
      if (isCustomCrop) {
        return {
          ...state,
          isCustomCrop: true,
          species: null,
          variety: null,
          archetype: null,
          growthType: null,
          lifecycleType: null,
          selectedPhaseCodes: null,
          selectedTaskCodes: null,
          selectedPropagationCode: null,
          adjustedPhases: null,
          adjustedTasks: null,
        }
      }
      return {
        ...state,
        isCustomCrop: false,
        customSpeciesName: '',
        customFamilyCode: null,
        isCustomVariety: false,
        customVarietyName: '',
        growthType: null,
        lifecycleType: null,
        selectedPhaseCodes: null,
        selectedTaskCodes: null,
        selectedPropagationCode: null,
        adjustedPhases: null,
        adjustedTasks: null,
      }
    }

    case CROP_MODAL_ACTION_TYPES.SET_CUSTOM_SPECIES_NAME:
      return { ...state, customSpeciesName: action.customSpeciesName }

    case CROP_MODAL_ACTION_TYPES.SET_CUSTOM_VARIETY_MODE: {
      const { isCustomVariety } = action
      return {
        ...state,
        isCustomVariety,
        variety: isCustomVariety ? null : state.variety,
        customVarietyName: isCustomVariety ? state.customVarietyName : '',
      }
    }

    case CROP_MODAL_ACTION_TYPES.SET_CUSTOM_VARIETY_NAME:
      return { ...state, customVarietyName: action.customVarietyName }

    case CROP_MODAL_ACTION_TYPES.SET_CUSTOM_FAMILY_CODE:
      return { ...state, customFamilyCode: action.customFamilyCode }

    case CROP_MODAL_ACTION_TYPES.SET_ARCHETYPE:
      return { ...state, archetype: action.archetype }

    case CROP_MODAL_ACTION_TYPES.RESET_FORM:
      return {
        ...initialState,
        bulkAdd: state.bulkAdd,
        newCropsQueue: state.newCropsQueue,
        userSelectedOrgId: state.userSelectedOrgId,
        userSelectedOrgName: state.userSelectedOrgName,
      }

    case CROP_MODAL_ACTION_TYPES.RESET:
      return { ...initialState }

    default:
      throw new Error(`Unknown action sent to CropModalContextReducer: ${action.type}`)
  }
}
